// Widget: Meine Aufgaben — die ersten 5 offenen Tasks (Projekt + persönlich), klickbar.

import { useEffect, useState } from "preact/hooks";
import { useLocation } from "wouter-preact";
import { Diamond, Circle, User as UserIcon } from "lucide-preact";
import { fetchMyOpenTasks, type DashboardTaskItem } from "../../lib/dashboardTasks";
import { formatDateShort } from "../../lib/format";

export function MyTasksWidget(): preact.JSX.Element {
  const [tasks, setTasks] = useState<DashboardTaskItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [, setLocation] = useLocation();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    void fetchMyOpenTasks().then(
      (items) => {
        if (cancelled) return;
        setTasks(items.slice(0, 5));
        setLoading(false);
      },
      () => {
        // Sonst blieb ein Ladefehler unsichtbar hinter "sauber" — ein leerer
        // Erfolg sieht identisch aus wie ein Fehlschlag, ohne diesen eigenen
        // Zustand.
        if (cancelled) return;
        setError(true);
        setLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading)
    return <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">Lädt…</p>;
  if (error)
    return (
      <p class="text-xs text-[color:var(--color-error)] italic">
        Konnte nicht geladen werden.
      </p>
    );
  if (tasks.length === 0)
    return (
      <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
        Keine offenen Aufgaben — sauber.
      </p>
    );

  return (
    <ul class="flex flex-col gap-1.5 text-sm">
      {tasks.map((t) => (
        <li key={t.uid}>
          <button
            type="button"
            onClick={() => setLocation(t.href)}
            class="w-full flex items-center gap-2 text-left rounded px-1.5 py-1 hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
          >
            {t.isMilestone ? (
              <Diamond size={14} class="text-[color:var(--color-prio-urgent)] shrink-0" />
            ) : t.source === "personal" ? (
              <UserIcon size={14} class="text-[color:var(--color-on-surface-variant)] shrink-0" aria-hidden="true" />
            ) : (
              <Circle size={14} class="text-[color:var(--color-on-surface-variant)] shrink-0" aria-hidden="true" />
            )}
            <span class="flex-1 min-w-0 truncate">{t.title}</span>
            {t.due && (
              <span class="shrink-0 text-label text-[color:var(--color-on-surface-variant)]">
                {formatDateShort(t.due)}
              </span>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}
